import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

export type PwaInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type AppContextValue = {
  deferredPrompt: PwaInstallPromptEvent | null;
  /** Call from a `beforeinstallprompt` handler; prevents default and stores the event for `promptInstall`. */
  captureInstallPrompt: (e: Event) => void;
  /** Opens the native install prompt if one was captured; returns null otherwise. */
  promptInstall: () => Promise<{ outcome: "accepted" | "dismissed" } | null>;
};

const AppContext = createContext<AppContextValue | null>(null);

export function PwaInstallProvider({ children }: { children: ReactNode }) {
  const deferredPromptRef = useRef<PwaInstallPromptEvent | null>(null);
  const [hasDeferredPrompt, setHasDeferredPrompt] = useState(false);

  const captureInstallPrompt = useCallback((e: Event) => {
    e.preventDefault();
    deferredPromptRef.current = e as PwaInstallPromptEvent;
    setHasDeferredPrompt(true);
  }, []);

  const promptInstall = useCallback(async (): Promise<{ outcome: "accepted" | "dismissed" } | null> => {
    const p = deferredPromptRef.current;
    if (!p) return null;
    await p.prompt();
    const userChoice = await p.userChoice;
    if (userChoice.outcome === "accepted") {
      deferredPromptRef.current = null;
      setHasDeferredPrompt(false);
      return userChoice;
    }
    return null;
  }, []);

  const deferredPrompt = hasDeferredPrompt ? deferredPromptRef.current : null;

  const value = useMemo(
    (): AppContextValue => ({ deferredPrompt, captureInstallPrompt, promptInstall }),
    [deferredPrompt, captureInstallPrompt, promptInstall],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function usePwaInstall() {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("usePwaInstall must be used within PwaInstallProvider");
  }
  return ctx;
}
